import React from 'react'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '~/lib/ui/table'
import { LinkButton } from '~/lib/ui/button'
import { useCandidate } from './CandidateProvider'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '~/util/api'

export const useCoachMessages = () => {
  const { candidate: { id: candidateId } = {} } = useCandidate()

  const queryPath = `/coach/dashboard_candidates/${candidateId}/messages.json`

  const queryFn = () => apiGet(queryPath).then((res) => res.data)
  const { data, error, isFetching } = useQuery({
    queryKey: ['messages', candidateId],
    queryFn,
    enabled: !!candidateId,
  })

  if (error) {
    console.error('Error retrieving coach message threads:', error)
    return null
  }

  const coachMessageThreads = data?.coachMessageThreads || []
  const mayInitiateMessage = data?.mayInitiateMessage || false
  return { coachMessageThreads, isFetching, mayInitiateMessage }
}

const MessagesSection = () => {
  const { candidate: { id: candidateId } = {} } = useCandidate()
  const { coachMessageThreads, mayInitiateMessage } = useCoachMessages()
  return (
    <>
      {mayInitiateMessage && (
        <div className="mb-4 flex flex-row justify-between gap-x-8">
          <LinkButton
            href={`/coach/messages?candidate_id=${candidateId}`}
            variant="coachcta"
          >
            Send Message
          </LinkButton>
          <div className="flex-grow">
            This page displays your individual message threads with your
            candidate. To view or send messages, click the ‘View’ link to open
            the Messages page.
          </div>
        </div>
      )}
      <Table
        className="w-full border-separate"
        tableClass="rounded-2xl border border-silver"
      >
        <MessagesHeading />
        <TableBody>
          {coachMessageThreads?.map((coachMessageThread) => (
            <MessageThreadRow
              key={coachMessageThread.id}
              coachMessageThread={coachMessageThread}
            />
          ))}
        </TableBody>
      </Table>
    </>
  )
}
export default MessagesSection

const MessageThreadRow = ({ coachMessageThread, ...props }) => {
  const {
    id,
    candidateName,
    subject,
    date,
    hasCandidateMessages,
    lastMessageId,
  } = coachMessageThread || {}

  return (
    <TableRow
      {...props}
      className="odd:bg-white even:bg-[#e2e2e2] even:bg-opacity-[29%]"
    >
      <TableCell>{candidateName}</TableCell>
      <TableCell>{subject}</TableCell>
      <TableCell>{date}</TableCell>
      <TableCell>
        <a
          href={`/coach/messages#${hasCandidateMessages ? id : `${id}-${lastMessageId}`} `}
          className="!underline"
        >
          View
        </a>
      </TableCell>
    </TableRow>
  )
}

const MessagesHeading = () => {
  const cells = ['Candidate', 'Subject', 'Date', '']
  return (
    <TableHeader className="h-6 bg-intoo-blue-medium">
      <TableRow className="text-lg text-white hover:bg-intoo-blue-medium">
        {cells.map((cell, index) => (
          <TableHead key={index} className="font-medium text-white">
            {cell}
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
  )
}
